/*
 * This component allows for API Controlled Table.
 * It implements an asynchronous filtering, pagination and search.
 * It is expected to update the browser URL after each change to the parameters of the table.
 * After each interaction from the user, the function "onUpdate" will be triggered and
 * the container of the component should update the params so it triggers a re-render.
 * When the component receives a new set of tableParams it will call the fetcher with the new
 * set and refresh the table.
 * The caller of this component is in charge of using Table with its desired columns
 * inside a render props function.
 */
import type {ReactElement, ReactNode} from 'react'
import {useState} from 'react'
import type {NavigateFunction} from 'react-router'
import {faChevronLeft, faChevronRight} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import type {LinksT, LinkPathT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {equalExtra, staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {numberToHumanFormat} from '@freckle/educator-entities/ts/common/helpers/string-helper'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {CenteredSpinner} from '@freckle/educator-materials/ts/components/spinner'
import {logError} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import {handleFetchingError} from '@freckle/school/ts/common/routers/app-router'
import type {CourseTabFilteringT} from './../course-tab'
import type {StudentTabFilteringT} from './../student-tab/student-tab'
import type {TeacherTabFilteringT} from './../teacher-tab'
import {tableFooter, tableFooterRight, filtersWrapper} from './api-controlled-table.module.scss'

type PaginationPanelProps = {
  components: {
    sizePerPageDropdown: ReactElement
    pageList: ReactElement
  }
}

type ExpandableTableOption = {
  ignoreSinglePage: boolean
  paginationPanel: (props: PaginationPanelProps) => ReactElement
  onSizePerPageList: (sizePerPage: number) => void
  sizePerPage: number
}

const DEFAULT_SIZE_PER_PAGE = 25

export type FilteringParamsT = StudentTabFilteringT | CourseTabFilteringT | TeacherTabFilteringT

export type SharedTableParamsT = {
  position?: string | null
  searchValue?: string | null
  limit?: number | null
}

export type TableParamsT<Filter extends FilteringParamsT> = {
  additionalFilters?: Filter | null
} & SharedTableParamsT

type Props<T, Filter extends FilteringParamsT> = {
  tableParams: TableParamsT<Filter>
  fetchRows: (tableParams: TableParamsT<Filter>) => Promise<{
    links: LinksT
    rows: Array<T>
    totalCount: number
  }>
  onUpdate: (tableParams: TableParamsT<Filter>) => void
  render: (options: ExpandableTableOption, rows: Array<T>) => ReactNode
  routerHistory: NavigateFunction
  filters?: ReactNode
  name: string
  selfRosteredTeachersSelections?: ReactNode
}

export function ApiControlledTabContainer<T, Filter extends FilteringParamsT>(
  props: Props<T, Filter>,
) {
  const {
    tableParams,
    fetchRows,
    onUpdate: onUpdateFromProps,
    render,
    routerHistory,
    filters,
    name,
    selfRosteredTeachersSelections,
  } = props

  const [rows, setRows] = useState<T[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [limit, setLimit] = useState<number>(tableParams.limit ?? DEFAULT_SIZE_PER_PAGE)
  const [links, setLinks] = useState<LinksT | null>(null)
  const [totalCount, setTotalCount] = useState<number>(0)

  useSafeEffectExtraDeps<{
    tableParams: TableParamsT<Filter>
    getRows: (tableParams: TableParamsT<Filter>) => void
  }>(
    ({tableParams, getRows}) => {
      getRows(tableParams)
    },
    [],
    {
      tableParams: equalExtra(tableParams),
      getRows: staticExtra(getRows),
    },
  )

  function onSizePerPageList(sizePerPage: number) {
    setLimit(sizePerPage)
    onUpdate()
  }

  function getRows(tableParams: TableParamsT<Filter>) {
    setIsLoading(true)

    const currentTableParams = prepareTableParamsWithDefaults(tableParams)

    fetchRows(currentTableParams)
      .then(({rows, links, totalCount}) => {
        setIsLoading(false)
        setRows(rows)
        setLinks(links)
        setTotalCount(totalCount)
      })
      .catch(err => {
        logError(new HttpError(err))
        handleFetchingError(err)
      })
  }

  function onUpdate(urlLink?: LinkPathT) {
    const newTableParams = createTableParams(tableParams, limit, '', urlLink)
    onUpdateFromProps(newTableParams)
  }

  function renderPaginationPanel(paginationProps: PaginationPanelProps) {
    const {components} = paginationProps

    /*
     * For now the prev button is controlled by the frontend
     * If there is a position param then show the prev button
     */
    const showPrevLink = tableParams.position !== null && tableParams.position !== undefined
    const prevButton = showPrevLink ? (
      <Button
        style="tertiary"
        onClick={() => {
          routerHistory(-1)
        }}
        dataTest="table-pagination-btn-prev"
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </Button>
    ) : null

    const nextLink = links?.next
    const nextButton =
      nextLink !== undefined ? (
        <Button
          style="tertiary"
          onClick={() => onUpdate(nextLink)}
          dataTest="table-pagination-btn-next"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      ) : null

    const SizeDropDownComponent = components.sizePerPageDropdown
    return (
      <div className={tableFooter}>
        <div>{SizeDropDownComponent}</div>
        <div className={tableFooterRight}>
          {prevButton}
          {nextButton}
        </div>
      </div>
    )
  }

  const options = {
    ignoreSinglePage: true,
    paginationPanel: renderPaginationPanel,
    onSizePerPageList,
    sizePerPage: limit,
  }

  const body = isLoading ? <CenteredSpinner /> : render(options, rows)

  const filterNode =
    filters !== null && filters !== undefined ? (
      <div className={filtersWrapper}>{filters}</div>
    ) : null

  const counterNode =
    selfRosteredTeachersSelections !== null && selfRosteredTeachersSelections !== undefined ? (
      <div>{selfRosteredTeachersSelections}</div>
    ) : (
      <p>
        {numberToHumanFormat(totalCount)} {dumbPluralize(totalCount, name)}
      </p>
    )

  return (
    <>
      {filterNode}
      {counterNode}
      {body}
    </>
  )
}

function createTableParams<Filter extends FilteringParamsT>(
  initialTableParams: TableParamsT<Filter>,
  newSizePerPage: number,
  localSearchValue: string,
  urlLink?: LinkPathT | null,
): TableParamsT<Filter> {
  if (urlLink !== null && urlLink !== undefined) {
    const paramsFromLink = parseUrlLink(urlLink)
    const {additionalFilters} = initialTableParams
    return {...paramsFromLink, additionalFilters}
  }
  const {position, limit, searchValue, additionalFilters} = initialTableParams

  const searchValueParams = localSearchValue.length > 0 ? {searchValue: localSearchValue} : {}

  // if limit or name has changed, remove position
  if (
    newSizePerPage !== limit ||
    (localSearchValue !== searchValue && localSearchValue.length > 0)
  ) {
    return {
      position: null,
      limit: newSizePerPage,
      ...searchValueParams,
      additionalFilters,
    }
  }

  return {
    position,
    limit,
    ...searchValueParams,
    additionalFilters,
  }
}

function parseUrlLink<Filter extends FilteringParamsT>(urlLink: string): TableParamsT<Filter> {
  const urlParts = urlLink.split('?')

  // Check directly if queryParams is defined to make type inference more explicit to Typescript
  if (urlParts.length !== 2) {
    return {
      position: null,
      limit: null,
      searchValue: null,
    }
  }

  const queryParams = urlParts[1] ?? ''
  const pairs = queryParams.split('&')

  const query: Record<string, string> = {}
  for (let i = 0; i < pairs.length; i++) {
    const pairString = pairs[i]
    if (pairString === undefined) continue

    const pair = pairString.split('=')
    if (pair[0] === undefined || pair[1] === undefined) continue
    const key = decodeURIComponent(pair[0])
    const value = decodeURIComponent(pair[1])
    query[key] = value
  }

  const {position, searchValue, limit} = query

  const limitParam = limit !== undefined ? {limit: parseInt(limit, 10)} : {}

  const searchValueParam = (searchValue?.length ?? 0) > 0 ? {searchValue} : {}

  return {
    position,
    ...limitParam,
    ...searchValueParam,
  }
}

function prepareTableParamsWithDefaults<Filter extends FilteringParamsT>(
  tableParams: TableParamsT<Filter>,
): TableParamsT<Filter> {
  // We always define the limit even if it is not set
  return {
    ...tableParams,
    limit: tableParams.limit ?? DEFAULT_SIZE_PER_PAGE,
  }
}

// Extremely naive pluralization function that works for all table names we currently have
function dumbPluralize(totalCount: number, name: string): string {
  if (totalCount === 1) {
    return name
  }

  return `${name}s`
}
